import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { supabase } from '../lib/supabase.js';
import { useAuth } from './AuthContext.jsx';
import { migratePlanData } from '../lib/draftModel.js';

/**
 * PresentationContext — multi-plan state, persisted to Supabase.
 *
 * Each user can own many presentations. The provider tracks:
 *
 *   plans      — lightweight list of {id, title, updatedAt, createdAt}
 *                used by the Plans screen and any "in progress" badges.
 *   activeId   — which plan is currently being edited; persisted to
 *                localStorage so it survives reloads on a given device.
 *   data       — the *full* JSON content of the active plan; the source
 *                of truth that every tool reads from and writes to.
 *
 * Saving is debounced (800ms after the last edit) so we don't hammer
 * the database while the user types. Saves and updates target whichever
 * row is "active" via a ref, so switching plans doesn't accidentally
 * write the new plan's first edit into the previously-active row.
 *
 * Tools (Logistics, Objective, Draft, …) call updateTool/clearTool
 * exactly the way they did when storage was localStorage. They don't
 * need to know about activeId or plans; the context handles that.
 *
 * MIGRATION (four-step refactor): when a plan's content is hydrated from
 * the database, it is passed through migratePlanData(). That folds any
 * legacy Brainstorm + Design state into the single combined Draft shape
 * (which still lives on the `design` slice, so the Builder pipeline is
 * untouched). The function is idempotent and identity-preserving — a plan
 * that needs no migration is returned unchanged — and it never deletes the
 * brainstorm slice. See src/lib/draftModel.js.
 *
 * Public API:
 *   { data, plans, activeId, loaded, savedFlash,
 *     updateTool, clearTool, clearAll,
 *     newPlan, openPlan, deletePlan, renamePlan }
 */

const ACTIVE_ID_KEY = 'pl_active_plan_id_v1';

export const initialPresentationState = () => ({
  id: 'local',
  createdAt: Date.now(),
  updatedAt: Date.now(),
  title: '',
  logistics: {},
  pages: {},
  objective: {},
  brainstorm: {},
  design: {},
  builder: {},
});

/**
 * Hydrate a Supabase row's saved data into full app state, applying the
 * Draft migration. Used by both the initial load effect and openPlan so
 * the two paths can't drift apart.
 */
function hydrate(rowData) {
  return migratePlanData({ ...initialPresentationState(), ...(rowData ?? {}) });
}

function planTitle(row) {
  // The plan's display name == the Presentation Name field on the
  // Logistics screen (logistics.eventName). data.title is checked as
  // a legacy fallback for any plans that were renamed via the older
  // inline-rename code path before this was unified.
  return (
    row?.data?.logistics?.eventName?.trim() ||
    row?.data?.title?.trim() ||
    'Untitled presentation'
  );
}

export const PresentationContext = createContext(null);

export function PresentationProvider({ children }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);          // [{id, title, updatedAt, createdAt}]
  const [activeId, setActiveIdState] = useState(() => {
    try { return localStorage.getItem(ACTIVE_ID_KEY) || null; } catch { return null; }
  });
  const [data, setData] = useState(initialPresentationState);
  const [loaded, setLoaded] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Mirror activeId into a ref so the debounced save always targets
  // the currently-active row, not a stale closure.
  const activeIdRef = useRef(null);
  useEffect(() => {
    activeIdRef.current = activeId;
    try {
      if (activeId) localStorage.setItem(ACTIVE_ID_KEY, activeId);
      else localStorage.removeItem(ACTIVE_ID_KEY);
    } catch { /* private mode etc. */ }
  }, [activeId]);

  // Helper: refresh the lightweight plans list from Supabase.
  const refreshPlans = useCallback(async (currentUser) => {
    const u = currentUser ?? user;
    if (!u) return [];
    const { data: rows, error } = await supabase
      .from('presentations')
      .select('id, data, created_at, updated_at')
      .eq('user_id', u.id)
      .order('updated_at', { ascending: false });
    if (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load plans list:', error);
      return [];
    }
    const list = (rows ?? []).map((r) => ({
      id: r.id,
      title: planTitle(r),
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    }));
    setPlans(list);
    return list;
  }, [user]);

  // ── On user change: load all plans, pick the active one, hydrate state ──
  useEffect(() => {
    if (!user) {
      setData(initialPresentationState());
      setActiveIdState(null);
      setPlans([]);
      setLoaded(false);
      return;
    }

    let cancelled = false;

    async function load() {
      const list = await refreshPlans(user);
      if (cancelled) return;

      // Pick which plan to make active. Order of preference:
      //   1. The one whose ID is stored in localStorage (if it still exists)
      //   2. Otherwise the most-recently-updated plan
      //   3. Otherwise no active plan (Plans screen will show empty state)
      const stashed = activeIdRef.current;
      const fromStash = stashed && list.find((p) => p.id === stashed);
      const chosen = fromStash || list[0] || null;

      if (!chosen) {
        setActiveIdState(null);
        setData(initialPresentationState());
        setLoaded(true);
        return;
      }

      // Fetch the full row for the chosen plan.
      const { data: row, error } = await supabase
        .from('presentations')
        .select('id, data')
        .eq('id', chosen.id)
        .single();
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load active plan:', error);
        setLoaded(true);
        return;
      }
      setActiveIdState(row.id);
      setData(hydrate(row.data));
      setLoaded(true);
    }

    load();
    return () => { cancelled = true; };
  }, [user, refreshPlans]);

  // ── Debounced save: 800ms after the last edit, UPDATE the active row ──
  useEffect(() => {
    if (!loaded || !activeIdRef.current || !user) return;

    const t = setTimeout(async () => {
      const targetId = activeIdRef.current;
      if (!targetId) return;
      const { error } = await supabase
        .from('presentations')
        .update({ data })
        .eq('id', targetId);
      if (error) {
        // eslint-disable-next-line no-console
        console.error('Save failed:', error);
        return;
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1200);

      // Refresh the plans list so the side panel reflects the new
      // updated_at and any title change. Cheap; one SELECT.
      refreshPlans(user);
    }, 800);

    return () => clearTimeout(t);
  }, [data, loaded, user, refreshPlans]);

  /** updateTool('logistics', { eventName: 'Q1 board meeting' }) */
  const updateTool = useCallback((tool, updates) => {
    setData((prev) => ({
      ...prev,
      [tool]: { ...prev[tool], ...updates },
      updatedAt: Date.now(),
    }));
  }, []);

  /** clearTool('logistics') — reset just one tool to empty. */
  const clearTool = useCallback((tool) => {
    setData((prev) => ({
      ...prev,
      [tool]: {},
      updatedAt: Date.now(),
    }));
  }, []);

  /** clearAll() — wipe the active plan's content but keep the row. */
  const clearAll = useCallback(() => {
    setData((prev) => ({ ...initialPresentationState(), title: prev.title }));
  }, []);

  /** newPlan() — create a fresh plan and switch to it. Returns the new id. */
  const newPlan = useCallback(async () => {
    if (!user) return null;
    const fresh = initialPresentationState();
    const { data: row, error } = await supabase
      .from('presentations')
      .insert({ user_id: user.id, data: fresh })
      .select('id, data, created_at, updated_at')
      .single();
    if (error || !row) {
      // eslint-disable-next-line no-console
      console.error('Failed to create plan:', error);
      return null;
    }
    // Switch active to the new plan and reset local data.
    setActiveIdState(row.id);
    setData(fresh);
    setLoaded(true);
    await refreshPlans(user);
    return row.id;
  }, [user, refreshPlans]);

  /** openPlan(id) — switch active plan and load its content. */
  const openPlan = useCallback(async (id) => {
    if (!user || !id) return;
    setLoaded(false);
    const { data: row, error } = await supabase
      .from('presentations')
      .select('id, data')
      .eq('id', id)
      .single();
    if (error || !row) {
      // eslint-disable-next-line no-console
      console.error('Failed to open plan:', error);
      setLoaded(true);
      return;
    }
    setActiveIdState(row.id);
    setData(hydrate(row.data));
    setLoaded(true);
  }, [user]);

  /** deletePlan(id) — remove a plan permanently. */
  const deletePlan = useCallback(async (id) => {
    if (!user || !id) return;
    const { error } = await supabase
      .from('presentations')
      .delete()
      .eq('id', id);
    if (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete plan:', error);
      return;
    }
    // If we just deleted the active plan, fall back to next most-recent.
    const fresh = await refreshPlans(user);
    if (id === activeIdRef.current) {
      const next = fresh[0];
      if (next) {
        await openPlan(next.id);
      } else {
        setActiveIdState(null);
        setData(initialPresentationState());
      }
    }
  }, [user, refreshPlans, openPlan]);

  /**
   * renamePlan(id, title) — update the displayed name of a plan.
   *
   * Writes to logistics.eventName, the same field the user fills in
   * on the Logistics screen. That keeps the Plans screen and Logistics
   * screen showing the same value — rename in either place and both
   * update.
   */
  const renamePlan = useCallback(async (id, title) => {
    if (!user || !id) return;
    // Read the current row's data, splice eventName into logistics, write back.
    const { data: row, error: rerr } = await supabase
      .from('presentations')
      .select('data')
      .eq('id', id)
      .single();
    if (rerr || !row) {
      // eslint-disable-next-line no-console
      console.error('Failed to read plan for rename:', rerr);
      return;
    }
    const newData = {
      ...(row.data ?? {}),
      logistics: {
        ...(row.data?.logistics ?? {}),
        eventName: title,
      },
    };
    const { error } = await supabase
      .from('presentations')
      .update({ data: newData })
      .eq('id', id);
    if (error) {
      // eslint-disable-next-line no-console
      console.error('Rename failed:', error);
      return;
    }
    // If we renamed the active plan, sync local state so Logistics
    // immediately reflects the change without needing a refetch.
    if (id === activeIdRef.current) {
      setData((prev) => ({
        ...prev,
        logistics: { ...prev.logistics, eventName: title },
      }));
    }
    refreshPlans(user);
  }, [user, refreshPlans]);

  const value = {
    data,
    plans,
    activeId,
    loaded,
    savedFlash,
    updateTool,
    clearTool,
    clearAll,
    newPlan,
    openPlan,
    deletePlan,
    renamePlan,
  };

  return (
    <PresentationContext.Provider value={value}>
      {children}
    </PresentationContext.Provider>
  );
}
